import { isPathwayStopCompleted } from "util/stopStatusUtils";
import { isWarehouseFixStop } from "util/warehouseReturnUtils";

export function getRouteTourType(route) {
  return (
    route?.delivery_timing_config?.tour_type ??
    route?.Tour?.tour_type ??
    route?.tour_type ??
    null
  );
}

export function isOneWayRoute(route) {
  return getRouteTourType(route) === "one_way";
}

export function normalizeRoutePathway(route) {
  return route;
}

const ROUTE_START_TYPES = new Set(["start_point", "pickup_end"]);

export function isRouteStartType(routeType) {
  return ROUTE_START_TYPES.has(routeType);
}

export function getDeliveryStartPathwayIndex(route) {
  const pathway = Array.isArray(route?.pathway) ? route.pathway : [];
  if (!pathway.length) return -1;

  const startPointIndex = pathway.findIndex((stop) => stop?.route_type === "start_point");
  if (startPointIndex >= 0) return startPointIndex;

  const pickupEndIndex = pathway.findIndex((stop) => stop?.route_type === "pickup_end");
  if (pickupEndIndex >= 0) return pickupEndIndex;

  return -1;
}

export function getDeliveryStartStop(route) {
  const index = getDeliveryStartPathwayIndex(route);
  if (index < 0) return null;
  return route.pathway[index] ?? null;
}

export function isStartStop(stop, index = null, route = null) {
  if (!stop || !isRouteStartType(stop?.route_type)) return false;
  if (route != null && index != null) {
    const startIndex = getDeliveryStartPathwayIndex(route);
    return startIndex >= 0 && index === startIndex;
  }
  return true;
}

export function isEndStop(stop) {
  return stop?.route_type === "end_point";
}

export function isCustomerStop(stop) {
  return stop?.id != null;
}

export function isFixStop(stop, route, index = null) {
  if (!stop || stop.id != null) return false;
  if (isStartStop(stop, index, route)) return false;
  if (isEndStop(stop)) return false;

  return true;
}

export function isCountableStop(stop, route, index = null) {
  if (!stop) return false;
  if (isStartStop(stop, index, route)) return false;
  if (isEndStop(stop)) return false;
  return isCustomerStop(stop) || isFixStop(stop, route, index);
}

export function getCountableStops(route) {
  const pathway = Array.isArray(route?.pathway) ? route.pathway : [];
  return pathway.filter((stop, index) => isCountableStop(stop, route, index));
}

export function buildStopLabelMap(route) {
  const pathway = Array.isArray(route?.pathway) ? route.pathway : [];
  const labelMap = new Map();

  let customerCount = 0;
  let fixCount = 0;

  pathway.forEach((stop, index) => {
    if (isStartStop(stop, index, route)) {
      labelMap.set(index, "L1");
      return;
    }

    if (isEndStop(stop)) {
      labelMap.set(index, "L2");
      return;
    }

    if (isFixStop(stop, route, index)) {
      fixCount += 1;
      labelMap.set(index, `F${fixCount}`);
      return;
    }

    if (isCustomerStop(stop)) {
      customerCount += 1;
      labelMap.set(index, String(customerCount));
    }
  });

  return labelMap;
}

export function getStopLabel(route, pathwayIndex) {
  if (!route || pathwayIndex == null || pathwayIndex < 0) return null;
  return buildStopLabelMap(route).get(pathwayIndex) ?? null;
}

export function getStopTypeLabel(stop, t, route, pathwayIndex) {
  if (route && Array.isArray(route.pathway) && pathwayIndex === route.pathway.length - 1) {
    if (stop?.id == null) {
      return t("End Location");
    }
  }
  if (stop?.route_type && stop.route_type.startsWith("pickup") && stop.route_type !== "pickup_end") {
    return t("Warehouse");
  }
  switch (stop?.route_type) {
    case "start_point":
      return t("Start Location");
    case "pickup_end":
      return t("Warehouse");
    case "customer":
      return t("Customer Stop");
    case "before_end":
      return t("Warehouse");
    case "after_end":
      return t("Warehouse");
    case "end_point":
      return stop?.id == null ? t("End Location") : t("Customer Stop");
    case "pickup_warehouse":
      return t("Warehouse");
    default:
      if (stop?.id != null) return t("Customer Stop");
      if (isWarehouseFixStop(stop)) return t("Warehouse");
      return t("Customer Stop");
  }
}

export function formatStopTypeWithLabel(stop, route, pathwayIndex, t) {
  return getStopTypeLabel(stop, t, route, pathwayIndex);
}

export function formatRouteStopCount(rowData) {
  const countable = getCountableStops(rowData);
  const completed = countable.filter((stop) => isPathwayStopCompleted(stop, rowData)).length;
  return `${completed} / ${countable.length}`;
}

function readLngLat(source) {
  if (!source) return null;

  const lat = Number(source.latitude);
  const lng = Number(source.longitude);
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return { latitude: lat, longitude: lng };
  }

  const geo = source.coordinates;
  if (geo?.coordinates?.length >= 2) {
    return {
      latitude: Number(geo.coordinates[1]),
      longitude: Number(geo.coordinates[0]),
    };
  }

  if (Array.isArray(geo) && geo.length >= 2) {
    return {
      latitude: Number(geo[1]),
      longitude: Number(geo[0]),
    };
  }

  return null;
}

function findSupplierLocationForPathwayStop(route, pathwayStop, pathwayIndex, allSupplierLocations = []) {
  const locations = Array.isArray(route?.supplier_locations) && route.supplier_locations.length > 0
    ? route.supplier_locations
    : allSupplierLocations;

  if (!Array.isArray(locations) || locations.length === 0) return null;

  const supplierId = pathwayStop?.supplier_location_id;
  if (supplierId == null) return null;

  const atIndex = locations[pathwayIndex];
  if (
    atIndex &&
    (atIndex.id === supplierId || atIndex.supplier_location_id === supplierId)
  ) {
    return atIndex;
  }

  const byName = locations.find(
    (location) =>
      (location.id === supplierId || location.supplier_location_id === supplierId) &&
      (!pathwayStop.name || !location.name || location.name === pathwayStop.name)
  );
  if (byName) return byName;

  const matching = locations.filter(
    (location) => location.id === supplierId || location.supplier_location_id === supplierId
  );
  if (matching.length === 1) return matching[0];

  const fixOccurrence = (route.pathway || [])
    .slice(0, pathwayIndex + 1)
    .filter((stop) => stop?.id == null && stop?.supplier_location_id === supplierId).length - 1;

  return matching[fixOccurrence] || matching[0] || null;
}

export function resolvePathwayStopLngLat(pathwayStop, route, pathwayIndex, allSupplierLocations = []) {
  if (!pathwayStop) return null;

  const fromPathway = readLngLat(pathwayStop);
  if (fromPathway) return fromPathway;

  const supplier = findSupplierLocationForPathwayStop(route, pathwayStop, pathwayIndex, allSupplierLocations);
  return readLngLat(supplier);
}

export function shouldShowPathwayStopOnMap(pathwayStop, route, pathwayIndex) {
  if (!pathwayStop) return false;
  if (isStartStop(pathwayStop, pathwayIndex, route)) return false;

  const isFix = isFixStop(pathwayStop, route, pathwayIndex);
  const isEnd = isEndStop(pathwayStop);

  if (isFix || isEnd) return true;

  const hasOrders =
    Array.isArray(pathwayStop.Orders) &&
    pathwayStop.Orders.length > 0;

  return hasOrders;
}

export function resolveAllRouteStopsWithOffsets(route, supplierLocations = []) {
  if (!route || !Array.isArray(route.pathway)) return [];

  const stopsWithCoords = route.pathway
    .map((pathwayStop, idx) => {
      const isStart = isStartStop(pathwayStop, idx, route);
      const isEnd = isEndStop(pathwayStop);
      const isFix = isFixStop(pathwayStop, route, idx);
      const hasOrders = Array.isArray(pathwayStop.Orders) && pathwayStop.Orders.length > 0;

      const showOnMap = isStart || isEnd || isFix || hasOrders;
      if (!showOnMap) return null;

      const coords = resolvePathwayStopLngLat(pathwayStop, route, idx, supplierLocations);
      if (!coords) return null;

      return {
        pathway: pathwayStop,
        idx,
        isStart,
        originalLat: coords.latitude,
        originalLng: coords.longitude,
        latitude: coords.latitude,
        longitude: coords.longitude,
      };
    })
    .filter(Boolean);

  const coordGroups = {};
  stopsWithCoords.forEach((stop) => {
    const key = `${stop.originalLat},${stop.originalLng}`;
    if (!coordGroups[key]) {
      coordGroups[key] = [];
    }
    coordGroups[key].push(stop);
  });

  Object.values(coordGroups).forEach((group) => {
    if (group.length === 2) {
      const radius = 0.00012;
      group[0].longitude = group[0].originalLng - radius;
      group[1].longitude = group[1].originalLng + radius;
    } else if (group.length > 2) {
      const radius = 0.00012;
      group.forEach((stop, i) => {
        const angle = (i * 2 * Math.PI) / group.length;
        stop.latitude = stop.originalLat + radius * Math.sin(angle);
        stop.longitude = stop.originalLng + radius * Math.cos(angle);
      });
    }
  });

  return stopsWithCoords;
}
